import { NodeUi } from './NodeUi';
import { NodeConnection, NodeConnectionNames } from 'dto/NodeConnection';

export class NodeConnectionUi {

    /**
     * True if a node is attached to this connection point.
     */
    public Implemented:boolean;

    /**
     * The node whose expression determines whether this connection is followed or not.
     */
    public From:NodeUi;

    /**
     * The node under which this connection is drawn.
     */
    public DrawFrom:NodeUi;

    /**
     * The node that is executed first when this connection is followed.
     */
    public To:NodeUi;

    /**
     * The DTO that contains all the necessary information, this will in the end be sent to the api.
     */
    public Dto:NodeConnection;

    /**
     * The X-coordinate of the start of the line of this connection
     * Relative to this.DrawFrom.X
     */
    public FromX:number;

    /**
     * The Y-coordinate of the start of the line of this connection
     * Relative to this.DrawFrom.Y
     */
    public FromY:number;

    /**
     * The X-coordinate of the end of the line of this connection
     * Relative to this.DrawFrom.X
     */
    public ToX:number;

    /**
     * The Y-coordinate of the end of the line of this connection
     * Relative to this.DrawFrom.Y
     */
    public ToY:number;
    
    /**
     * The X-coordinate of the left side of the drawn connection point (midpoint-dotwidth/2).
     * Relative to this.DrawFrom.X
     */
    public PointX:number;

    /**
     * The Y-coordinate of the top of the drawn connection point (midpoint-dotheight/2).
     * Relative to this.DrawFrom.Y
     */
    public PointY:number;

    /**
     * Highlight this node
     */
    public Highlight:boolean;

    constructor(from:NodeUi, dto:NodeConnection, drawFrom:NodeUi) {
        if (dto.Identifier == null)
            dto.Identifier = from.dto.Identifier;
        else if (from.dto.Identifier != dto.Identifier)
            throw Error('Cannot construct NodeConnectionUi with non-matching parameters');
        this.Dto = dto;
        this.From = from;
        this.Reset(drawFrom);
    }

    public toString():string{
        if (this.Dto.Value != null)
            return `${this.Dto.Name}=${this.Dto.Value} of ${this.From}`;
            return `${this.Dto.Name} of ${this.From}`;
    }

    /**
     * Calculates all the X and Y coordinates for this connection (required for drawing lines and dots).
     */
    public UpdateDrawOffsets() {
        this.FromX = this.DrawFrom.X + this.DrawFrom.ColumnWidth/2 + this.DrawFrom.UiWidth/2;
        this.FromY = this.DrawFrom.Y + this.DrawFrom.UiHeight;
        if (this.Implemented)
        {
            // Centered on top of the node.
            this.ToX = this.To.X + this.To.ColumnWidth/2 + this.DrawFrom.UiWidth/2;
            if (this.To.Parents.length > 1)
            {
                // The target is a multi-parent node. We should arrange this connection so it does not overlap the other connections that connect to this node.
                var coConnections = new Array<NodeConnectionUi>();
                // Fill the coConnections array with all connections that target this.To, from the To.Parents nodes.
                this.To.Parents.forEach(node => coConnections.push(...node.Connections.filter(conn => conn.To == this.To)));
                // Calculate an X position such that all coConnections will be distributed centered on the node centre.
                this.ToX -= coConnections.length * OFFSET_SIB_X / 2;
                // Sort coConnections by X coordinate of the parent.
                // This ensures that if A and B are parents of C, and A is to the left of B,
                // then the connection of A on C is to the left of the connection of B on C.
                coConnections.sort((a, b) => a.DrawFrom.X - b.DrawFrom.X);
                // Find the index of this node, and calculate the X position for this specific node among its siblings.
                this.ToX += OFFSET_SIB_X * coConnections.findIndex(node => node === this);
            }
            this.ToY = this.To.Y;
        }
        else
        {
            if (this.Dto.Name == NodeConnectionNames.ALWAYS_TRIGGERED)
            {
                this.ToX = this.FromX;
                this.ToY = this.FromY;
                // Unimplemented ALWAYS nodes.
                // Draw the "Always" connection point a bit below the default location so it doesn't touch the other connection line.
                if (this.DrawFrom.Childs.length > 0)
                    this.ToY += OFFSET_UA_Y;
                // Draw the "Always" connection point a bit to the left if it would otherwise hit a connection line.
                if (this.DrawFrom.Childs.some(child => (child.X + child.ColumnWidth/2) == (this.DrawFrom.X + this.DrawFrom.ColumnWidth/2)))
                    this.ToX += OFFSET_UA_X;

            }
            else
            {
                // Unimplemented RESULT or ERROR nodes.
                // Find the index of this connection among the other unimplemented not-ALWAYS connections of this.DrawFrom
                let index = this.DrawFrom.Connections.filter(x => !x.Implemented && x.Dto.Name != NodeConnectionNames.ALWAYS_TRIGGERED).findIndex(x => x === this);
                // Give this connection an X position based on its index among unimplemented siblings.
                this.FromX += -this.DrawFrom.UiWidth/2 + OFFSET_U_X_BETWEEN*index + OFFSET_U_X;
                this.ToX = this.FromX;
                this.ToY = this.FromY + OFFSET_U_Y;
            }
        }
        this.PointX = this.ToX+OFFSET_P_X
        this.PointY = this.ToY+OFFSET_P_Y
    }

    public Reset(drawFrom:NodeUi) {
        this.DrawFrom = drawFrom;
        this.To = this.DrawFrom.Childs.find(node => node.dto.TriggeredBy.some(trigger => trigger.Identifier == this.Dto.Identifier && trigger.Name == this.Dto.Name && this.Dto.Value == trigger.Value));
        this.Implemented = this.To != null;
        this.FromX = 0;
        this.FromY = 0;
        this.ToX = 0;
        this.ToY = 0;
        this.PointX = 0;
        this.PointY = 0;
    }
}
/** X-distance between two Unimplemented (not Always) node connections */
const OFFSET_U_X_BETWEEN = 17;
/** X-distance from the left side of the DrawFrom node to the X-coordinate of the Unimplemented (not Always) connection line */
const OFFSET_U_X = 20;
/** Y-distance from the bottom of the DrawFrom node to the end of an Unimplemented (not Always) connection */
const OFFSET_U_Y = 15;
/** X-distance from the x-axis midpoint of the DrawFrom node to the end of an Unimplemented Always connection */
const OFFSET_UA_X = -15;
/** Y-distance from the bottom of the DrawFrom node to the end of an Unimplemented Always connection */
const OFFSET_UA_Y = 15;
/** X-distance from the left side of the connection point icon to the end of the connection line. */
const OFFSET_P_X = -7;
/** Y-distance from the top of the connection point icon to the end of the connection line. */
const OFFSET_P_Y = -7;
/** X-distance between two sibling connection points attached to a multi-parent node. */
const OFFSET_SIB_X = 20;


// WEBPACK FOOTER //
// C:/git/flowdesigner/src/classes/NodeConnectionUi.ts